import {useMutation} from '@apollo/client';
import {Button} from '@dabafinance/react-components';
import {CREATE_IPO} from 'graphql/mutations/assetManagement';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import {handleClearIpoState} from 'redux/store/assetClass';
import {currencySymbols} from 'utils/mock';
import {buildBaseInstrumentInput} from '../modules/buildBaseInstrumentInput';

// The seven subscription dates, sent as ISO strings.
const IPO_DATES = [
  'offerAnnouncementDate',
  'subscriptionStartDate',
  'subscriptionEndDate',
  'allocationAnnouncementDate',
  'refundDate',
  'expectedListingDate',
  'sellUnlockDate',
];

export const CreateIpoFinishModal = ({handleClose}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {assetId: assetKey} = useParams();
  const draft = useSelector(state => state?.assetClass);

  const [createIpo, {loading}] = useMutation(CREATE_IPO);

  const handleCreateIpo = () => {
    // Step 1 writes the base instrument, step 2 writes the IPO fields — plus
    // ticker and isin, which are entered on the IPO screen but belong to the
    // base input. Every key below exists on IpoInput; nothing else does.
    const instrument = draft?.ipoInstruments ?? {};
    const {
      ticker,
      isin,
      offerPrice,
      priceRangeLow,
      priceRangeHigh,
      totalSharesOnOffer,
      dabaAllocationShares,
      ipoStatus,
      exclusiveOnDaba,
      fullAllocationLikely,
      highlights,
      tentativeDates,
      ...rest
    } = draft?.ipo ?? {};

    const dates = Object.fromEntries(
      IPO_DATES.filter(key => rest[key]).map(key => [
        key,
        moment(rest[key]).format(),
      ]),
    );

    createIpo({
      variables: {
        input: {
          offerPrice,
          priceRangeLow,
          priceRangeHigh,
          totalSharesOnOffer,
          dabaAllocationShares: dabaAllocationShares ?? null,
          ipoStatus,
          exclusiveOnDaba: exclusiveOnDaba ?? false,
          fullAllocationLikely: fullAllocationLikely ?? false,
          ...(highlights?.length && {highlights}),
          // The keys here are an explicit whitelist, so a new IpoInput field
          // is dropped unless it is named. Sent even when empty: the service
          // treats the list as a replace, and `[]` is how "none" is said.
          tentativeDates: tentativeDates ?? [],
          // `exchange` is offered on IpoInput as well as on the base input;
          // send it on both so whichever the resolver reads is populated.
          ...(instrument?.exchange && {exchange: instrument.exchange}),
          ...dates,
          baseFinancialInstrumentInput: buildBaseInstrumentInput({
            key: 'IPO',
            assetKey,
            instrument,
            ticker,
            isin,
            currency: currencySymbols[instrument?.currency],
          }),
        },
      },
    })
      .then(({data, errors}) => {
        const result = data?.createIpo;
        if (result?.__typename === 'Error') {
          toast.error(result.message);
        } else if (errors) {
          toast.error(errors[0].message);
        } else {
          toast.success('Created Successfully');
          dispatch(handleClearIpoState());
          navigate(`/dashboard/asset-management/class/${assetKey}/instruments`);
        }
      })
      .catch(error => toast.error(error.message));
  };

  return (
    <div className="confirm-modal">
      <div>
        <h1>Create IPO</h1>
        <p>
          Please make sure the details are correctly inputted before proceeding
        </p>
      </div>
      <div className="buttons">
        <Button
          disabled={loading}
          label={!loading ? 'Create' : 'Loading...'}
          onClick={handleCreateIpo}
        />
        <Button type="secondary" label="No" onClick={handleClose} />
      </div>
    </div>
  );
};

export default CreateIpoFinishModal;
